import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    analyticsAddBankAccountFlowViewed,
    analyticsAddBankAccountOTPViewed,
    analyticsConfirmBankAccountFlowSuccess,
    analyticsConfirmBankAccountFlowViewed,
} from '@perpay-web/fintech/actions/analytics/addBank';
import { sendMFACode } from '@perpay-web/fintech/actions/entities/sendMFACode';
import { fetchUserInfoForDataModule } from '@perpay-web/fintech/actions/entities/userInfo';
import AddBankAccountFlow from '@perpay-web/fintech/components/composite/AddBankAccountFlow/AddBankAccountFlow';
import { getUserInfo } from '@perpay-web/fintech/dataModules/fetchUserInfo';

/**
 * @typedef {Object} BankAccountData
 * @property {string} uuid
 * @property {string} status
 * @property {string} account
 * @property {string} routing
 * @property {string} bankName
 */

/**
 * @param {Object} props
 * @param {() => void} props.onClose
 * @param {(data: BankAccountData) => void} props.onSuccess
 * @param {() => void} props.onFailure
 * @param {('card')} [props.buttonTheme] - The theme of the button to use for the add bank account button.
 * @param {boolean} [props.skipMFA] - Whether to skip MFA for the add bank account flow.
 */
export const AddBankAccountFlowContainer = ({
    onClose,
    onSuccess,
    onFailure,
    buttonTheme,
    skipMFA,
}) => {
    const dispatch = useDispatch();
    const dispatchFetchUserInfo = useCallback(() => {
        if (!skipMFA) {
            dispatch(fetchUserInfoForDataModule());
        }
    }, [dispatch, skipMFA]);
    const userInfo = useSelector(getUserInfo);

    const dispatchAddBankAccountFlowViewed = useCallback(() => {
        dispatch(analyticsAddBankAccountFlowViewed());
    }, [dispatch]);

    const dispatchConfirmBankAccountFlowViewed = useCallback(() => {
        dispatch(analyticsConfirmBankAccountFlowViewed());
    }, [dispatch]);

    const dispatchAddBankAccountOTPViewed = useCallback(() => {
        dispatch(analyticsAddBankAccountOTPViewed());
    }, [dispatch]);

    const dispatchConfirmBankAccountFlowSuccess = useCallback(() => {
        dispatch(analyticsConfirmBankAccountFlowSuccess());
    }, [dispatch]);

    const dispatchSendMFACode = useCallback(() => {
        dispatch(sendMFACode());
    }, [dispatch]);

    return (
        <AddBankAccountFlow
            onClose={onClose}
            onSuccess={onSuccess}
            onFailure={onFailure}
            userInfo={userInfo}
            dispatchSendMFACode={dispatchSendMFACode}
            dispatchFetchUserInfo={dispatchFetchUserInfo}
            dispatchAddBankAccountFlowViewed={dispatchAddBankAccountFlowViewed}
            dispatchConfirmBankAccountFlowViewed={
                dispatchConfirmBankAccountFlowViewed
            }
            dispatchAddBankAccountOTPViewed={dispatchAddBankAccountOTPViewed}
            dispatchConfirmBankAccountFlowSuccess={
                dispatchConfirmBankAccountFlowSuccess
            }
            buttonTheme={buttonTheme}
            skipMFA={skipMFA}
        />
    );
};
